import { useMemo, useState } from 'react';
import { FilterDropdown } from '@ui';
import {
  ArrivalHubSettingsType,
  DeploymentHubSettingsType,
  SuggestedSettingsEnum,
} from '@lp/features/RoutePlanner/types';
import { useHubsQuery } from '@/queries/hub';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { SuggestedSelectTrigger } from './SuggestedSelectTrigger';
import { getHubSettingsText } from './utils';

type HubsDropdownProps = {
  type: SuggestedSettingsEnum.DeploymentHub | SuggestedSettingsEnum.ArrivalHub;
  data: DeploymentHubSettingsType | ArrivalHubSettingsType;
  isDisabled?: boolean;
  handleSettingsChange: (data: DeploymentHubSettingsType | ArrivalHubSettingsType) => void;
};

export const HubsDropdown = ({ type, data, isDisabled, handleSettingsChange }: HubsDropdownProps) => {
  const [searchValue, setSearchValue] = useState('');
  const { data: hubsList, isLoading: isHubsLoading } = useHubsQuery();
  const { nonEuCountryCodes, isLoading: isCountriesLoading } = useCountriesData();

  const hubSelectList = useMemo(
    () =>
      (hubsList?.items || [])
        .filter((hub) => nonEuCountryCodes.has(hub.location.country))
        .map((hub) => ({
          value: hub.id,
          name: `${hub.name} (${hub.code})`,
        })),
    [hubsList, nonEuCountryCodes],
  );

  const selectedHub = useMemo(() => {
    return hubSelectList.find((h) => h.value === data.value);
  }, [hubSelectList, data.value]);

  const hubsSearchList = useMemo(() => {
    if (searchValue) {
      return hubSelectList.filter((h) => {
        return h.name.toLowerCase().includes(searchValue.toLowerCase());
      });
    }

    return hubSelectList;
  }, [hubSelectList, searchValue]);

  const isDeploymentHubSelect = type === SuggestedSettingsEnum.DeploymentHub;

  return (
    <FilterDropdown
      title={isDeploymentHubSelect ? 'Deployment Hub' : 'Arrival Hub'}
      placement="top-start"
      items={hubsSearchList}
      isDisabled={isDisabled}
      isLoading={isHubsLoading || isCountriesLoading}
      selectedKeys={data.value ? [data.value] : []}
      renderCustomTrigger={(isOpen) => (
        <SuggestedSelectTrigger
          title={isDeploymentHubSelect ? 'Deployment Hub:' : 'Arrival Hub:'}
          value={selectedHub?.name ?? getHubSettingsText(data.value)}
          isOpen={isOpen}
        />
      )}
      mode="single"
      onSearchValue={setSearchValue}
      onSelectedKeysChange={(value) => {
        handleSettingsChange({
          type,
          value: value[0] ?? null,
        });
      }}
    />
  );
};
